import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { RootState, useAppDispatch } from "@store/store";
import { setLoanStatus } from "@store/slices/registerSlice";
import { useLazyGetLoanApplicationByIdQuery } from "@api/applications";
import { LoanStatus } from "@interfaces/catalogs";
import useInterval from "@hooks/useInterval";

/** Mismo periodo de sondeo que las salas de espera legacy. */
const SECONDS_TO_FETCH = 10;

/**
 * Sondea la solicitud cada 10 s mientras el usuario espera una decisión del backend (sala de espera,
 * revisión): el desenlace se ramifica (oferta, rechazo, revisión) y solo el backend sabe cuál tocó, así que
 * no hay un "siguiente paso" que calcular con `useStatusConfig`. Mientras el status siga siendo
 * `waitingStatusCode` no hace nada; en cuanto cambia, navega a la URL de ese status.
 */
const useLoanStatusPolling = (waitingStatusCode: string, component: string) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { pathname, search } = useLocation();

  const application = useSelector((state: RootState) => state.register.application);
  const statusConfig = useSelector((state: RootState) => state.app.status_config);
  const [getLoanApplication] = useLazyGetLoanApplicationByIdQuery();

  const syncStatus = async () => {
    try {
      const loan_application = await getLoanApplication(application.id).unwrap();
      const statusCode = loan_application?.status_catalog?.code || loan_application?.current_status?.code || "";

      if (!statusCode) return;

      dispatch(setLoanStatus(statusCode as LoanStatus));

      if (statusCode === waitingStatusCode) return;

      const targetUrl =
        statusConfig.find((item) => item.code === statusCode)?.url ||
        loan_application?.status_catalog?.url_actual ||
        loan_application?.current_status?.url ||
        "";

      if (targetUrl && targetUrl !== `${pathname}${search}`) {
        navigate(targetUrl, { replace: true });
      }
    } catch (error) {
      // Es un sondeo cada 10 s: un fallo no interrumpe al usuario, se reintenta en el siguiente.
      Sentry.captureException(error, { extra: { component: `${component}-syncStatus` } });
    }
  };

  const loanInterval = useInterval(syncStatus, SECONDS_TO_FETCH);

  useEffect(() => {
    loanInterval.setIsActiveInterval(true);

    return () => {
      loanInterval.setIsActiveInterval(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useLoanStatusPolling;
